import React, { JSX, useEffect, useRef, useState } from 'react';
import { LatLng, LocationFields } from 'components/types/MapTypes';

type MarkersAndClustersProps = {
  onMarkerClick: (
    position: [number, number],
    locationsAtPoint: Array<LocationFields>
  ) => void;
  results?: Array<LocationFields>;
  activeMarker: LatLng;
  map?: any;
};

type GroupedPoint = {
  key: string;
  latitude: number;
  longitude: number;
  locationsAtPoint: Array<LocationFields>;
  isActive: boolean;
};

type MarkerFeatureProperties = {
  key: string;
  latitude: number;
  longitude: number;
  locationsAtPoint: Array<LocationFields>;
  isActive: boolean;
};

const DEFAULT_MARKER_IMAGE_ID = 'default-location-marker';
const CLUSTER_MARKER_IMAGE_ID = 'cluster-location-marker';

const getShapeCoordinates = (shape: any): [number, number] | null => {
  if (shape && typeof shape.getCoordinates === 'function') {
    return shape.getCoordinates();
  }

  if (Array.isArray(shape?.geometry?.coordinates)) {
    return shape.geometry.coordinates as [number, number];
  }

  if (shape && typeof shape.getProperties === 'function') {
    const props = shape.getProperties();

    if (props?.longitude && props?.latitude) {
      return [Number(props.longitude), Number(props.latitude)];
    }
  }

  return null;
};

const getShapeProperties = (shape: any): any => {
  if (shape && typeof shape.getProperties === 'function') {
    return shape.getProperties();
  }

  return shape?.properties || {};
};

const MarkersAndClusters = ({
  onMarkerClick,
  results = [],
  activeMarker,
  map,
}: MarkersAndClustersProps): JSX.Element => {
  const [atlasLib, setAtlasLib] = useState<any>(null);
  const onMarkerClickRef = useRef(onMarkerClick);
  const dataSourceRef = useRef<any>(null);
  const clusterSymbolLayerRef = useRef<any>(null);
  const pointLayerRef = useRef<any>(null);
  const clusterClickHandlerRef = useRef<((e: any) => void) | null>(null);
  const pointClickHandlerRef = useRef<((e: any) => void) | null>(null);
  const [groupedPoints, setGroupedPoints] = useState<GroupedPoint[]>([]);

  useEffect(() => {
    onMarkerClickRef.current = onMarkerClick;
  }, [onMarkerClick]);

  useEffect(() => {
    const grouped = new Map<string, Array<LocationFields>>();

    for (const result of results) {
      if (!result.latitude || !result.longitude) continue;

      const key = `${result.latitude},${result.longitude}`;
      const current = grouped.get(key) || [];
      current.push(result);
      grouped.set(key, current);
    }

    const newGrouped = Array.from(grouped.entries()).map(
      ([key, locationsAtPoint]) => {
        const first = locationsAtPoint[0];
        const latitude = parseFloat(first.latitude);
        const longitude = parseFloat(first.longitude);

        const isActive =
          activeMarker?.latitude !== undefined &&
          activeMarker?.longitude !== undefined &&
          Number(activeMarker.latitude) === latitude &&
          Number(activeMarker.longitude) === longitude;

        return {
          key,
          latitude,
          longitude,
          locationsAtPoint,
          isActive,
        };
      }
    );

    setGroupedPoints(newGrouped);
  }, [results, activeMarker]);

  useEffect(() => {
    let mounted = true;

    const loadAtlas = async () => {
      if (typeof window === 'undefined') return;

      try {
        const atlasModule = await import('azure-maps-control');
        const atlas = atlasModule.default || atlasModule;

        if (!mounted) return;

        setAtlasLib(atlas);
      } catch (error) {
        console.error(
          'Failed to load Azure Maps SDK in MarkersAndClusters:',
          error
        );
      }
    };

    loadAtlas();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!atlasLib || !map || dataSourceRef.current) return;

    let disposed = false;

    const initializeLayers = async () => {
      const dataSource = new atlasLib.source.DataSource(undefined, {
        cluster: true,
        clusterRadius: 45,
        clusterMaxZoom: 14,
      });

      dataSourceRef.current = dataSource;
      map.sources.add(dataSource);

      try {
        await Promise.all([
          map.imageSprite.add(
            DEFAULT_MARKER_IMAGE_ID,
            '/images/pld-map-location.svg'
          ),
          map.imageSprite.add(
            CLUSTER_MARKER_IMAGE_ID,
            '/images/pld-map-cluster.svg'
          ),
        ]);
      } catch (error) {
        console.error('Failed to load custom Azure marker images:', error);
      }

      if (disposed || !dataSourceRef.current) return;

      const clusterSymbolLayer = new atlasLib.layer.SymbolLayer(
        dataSource,
        undefined,
        {
          filter: ['has', 'point_count'],
          iconOptions: {
            image: CLUSTER_MARKER_IMAGE_ID,
            allowOverlap: true,
            ignorePlacement: true,
            anchor: 'center',
            size: ['case', ['==', ['get', 'isActive'], true], 1.1, 0.25],
          },
        }
      );

      const pointLayer = new atlasLib.layer.SymbolLayer(dataSource, undefined, {
        filter: ['!', ['has', 'point_count']],
        iconOptions: {
          image: DEFAULT_MARKER_IMAGE_ID,
          allowOverlap: true,
          ignorePlacement: true,
          anchor: 'center',
          size: ['case', ['==', ['get', 'isActive'], true], 0.25, 0.25],
        },
      });

      clusterSymbolLayerRef.current = clusterSymbolLayer;
      pointLayerRef.current = pointLayer;

      map.layers.add([clusterSymbolLayer, pointLayer]);

      const handleClusterClick = (e: any) => {
        const shape = e.shapes?.[0];
        if (!shape || !dataSourceRef.current) return;

        const props = getShapeProperties(shape);
        const clusterId = props?.cluster_id;
        const coordinates = getShapeCoordinates(shape);

        if (clusterId == null || !coordinates) return;

        dataSourceRef.current
          .getClusterExpansionZoom(clusterId)
          .then((clusterZoom: number) => {
            map.setCamera({
              center: coordinates,
              zoom: Math.min(clusterZoom, 17),
              type: 'ease',
              duration: 300,
            });
          });
      };

      const handlePointClick = (e: any) => {
        const shape = e.shapes?.[0];
        if (!shape) return;

        const props = getShapeProperties(shape) as MarkerFeatureProperties;

        onMarkerClickRef.current(
          [props.longitude, props.latitude],
          props.locationsAtPoint || []
        );
      };

      clusterClickHandlerRef.current = handleClusterClick;
      pointClickHandlerRef.current = handlePointClick;

      map.events.add('click', clusterSymbolLayer, handleClusterClick);
      map.events.add('click', pointLayer, handlePointClick);
    };

    initializeLayers();

    return () => {
      disposed = true;

      try {
        if (clusterClickHandlerRef.current) {
          map.events.remove('click', clusterClickHandlerRef.current);
        }

        if (clusterSymbolLayerRef.current && clusterClickHandlerRef.current) {
          map.events.remove(
            'click',
            clusterSymbolLayerRef.current,
            clusterClickHandlerRef.current
          );
        }

        if (pointLayerRef.current && pointClickHandlerRef.current) {
          map.events.remove(
            'click',
            pointLayerRef.current,
            pointClickHandlerRef.current
          );
        }

        if (clusterSymbolLayerRef.current) {
          map.layers.remove(clusterSymbolLayerRef.current);
          clusterSymbolLayerRef.current = null;
        }

        if (pointLayerRef.current) {
          map.layers.remove(pointLayerRef.current);
          pointLayerRef.current = null;
        }

        if (dataSourceRef.current) {
          map.sources.remove(dataSourceRef.current);
          dataSourceRef.current = null;
        }

        clusterClickHandlerRef.current = null;
        pointClickHandlerRef.current = null;
      } catch (error) {
        console.error('MarkersAndClusters cleanup failed:', error);
      }
    };
  }, [atlasLib, map]);

  useEffect(() => {
    if (!atlasLib || !dataSourceRef.current) return;

    const features = groupedPoints.map(point => {
      return new atlasLib.data.Feature(
        new atlasLib.data.Point([point.longitude, point.latitude]),
        {
          key: point.key,
          latitude: point.latitude,
          longitude: point.longitude,
          locationsAtPoint: point.locationsAtPoint,
          isActive: point.isActive,
        }
      );
    });

    dataSourceRef.current.clear();

    if (features.length > 0) {
      dataSourceRef.current.add(features);
    }
  }, [atlasLib, groupedPoints]);

  return <></>;
};

export default MarkersAndClusters;
